import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { loadConfig } from '../api/data-qa-api';
import { ConfigSummary, LoadResult } from '../data-qa-types';

// Every model's load, keyed by configPath: its result once it arrives, undefined while it
// loads, null if it failed. Observes the same ['mmmDataQALoad', cid, configPath] keys
// usePrefetchOtherModels warms and useSectionDataInvariance reads (React Query dedupes),
// so it adds no requests. Gated on the active model having loaded, like those hooks, so it
// never competes with the active load.
export const useModelLoadStates = (
  cid: string | undefined,
  configs: ConfigSummary[],
  activeModelLoaded: boolean
): Map<string, LoadResult | null | undefined> => {
  // Memoized: a new `queries` array every render churns useQueries' observers.
  const queries = useMemo(
    () =>
      configs.map(config => ({
        queryKey: ['mmmDataQALoad', cid, config.configPath],
        queryFn: () => loadConfig(cid ?? '', config.configPath),
        staleTime: Infinity,
        enabled: Boolean(cid) && activeModelLoaded
      })),
    [configs, cid, activeModelLoaded]
  );
  const results = useQueries({ queries });

  return useMemo(() => {
    const byPath = new Map<string, LoadResult | null | undefined>();
    configs.forEach((config, i) => {
      const result = results[i];
      byPath.set(config.configPath, result?.isError ? null : result?.data);
    });
    return byPath;
  }, [configs, results]);
};

// The loads that have arrived so far, for the About tab's cross-model summary.
export const useModelLoads = (
  cid: string | undefined,
  configs: ConfigSummary[],
  activeModelLoaded: boolean
): Map<string, LoadResult> => {
  const states = useModelLoadStates(cid, configs, activeModelLoaded);
  return useMemo(() => {
    const loads = new Map<string, LoadResult>();
    states.forEach((load, path) => {
      if (load) {
        loads.set(path, load);
      }
    });
    return loads;
  }, [states]);
};
